import { InlineStack, Select } from '@shopify/polaris';
import {
  AddOnOptionDef,
  AddOnOptionVariant,
  availableOptionValues,
  optionValueOf,
  pickVariantForOptionChange,
  variantsHaveOptions,
} from '@/utils/addonOptions';

interface AddOnOptionSelectsProps {
  options?: AddOnOptionDef[];
  variants: AddOnOptionVariant[];
  /** currently selected variant id */
  value: string;
  onChange: (variantId: string) => void;
  /** Optional per-variant label suffix for the legacy single-select fallback (e.g. price). */
  legacyLabel?: (variant: AddOnOptionVariant) => string;
}

/**
 * One Select per Shopify product option (Colour, Side, Size...). Falls back to a
 * single "Option" select of variant titles when selectedOptions are unavailable.
 */
export default function AddOnOptionSelects({
  options,
  variants,
  value,
  onChange,
  legacyLabel,
}: AddOnOptionSelectsProps) {
  if (variants.length <= 1) return null;

  if (!options || options.length === 0 || !variantsHaveOptions(variants)) {
    return (
      <div style={{ minWidth: 220 }}>
        <Select
          label="Option"
          options={variants.map((v) => ({ label: legacyLabel ? legacyLabel(v) : v.title, value: v.id }))}
          value={value}
          onChange={onChange}
        />
      </div>
    );
  }

  const current = variants.find((v) => v.id === value) ?? variants[0];

  return (
    <InlineStack gap="300" blockAlign="end" wrap>
      {options.map((opt) => {
        const values = availableOptionValues(opt, variants);
        if (values.length === 0) return null;
        return (
          <div key={opt.name} style={{ minWidth: 120 }}>
            <Select
              label={opt.name}
              options={values.map((val) => ({ label: val, value: val }))}
              value={optionValueOf(current, opt.name) ?? values[0]}
              onChange={(val) => {
                const next = pickVariantForOptionChange(variants, current.id, opt.name, val);
                if (next) onChange(next.id);
              }}
            />
          </div>
        );
      })}
    </InlineStack>
  );
}
